import Vue, { CreateElement, VNode } from '@src/vue'

import { Component } from 'vue-property-decorator'

import { Annotate } from './../../decorators/annotate'

import './pattern-library.sass'

import 'prismjs/prism.js'

import 'prismjs/components/prism-javascript.js'
import 'prismjs/components/prism-jsx.js'

import 'prismjs/themes/prism.css'

import Prism from 'vue-prism-component'

import {
  H4,
  H6,
  Body1
} from '@src/elements/typography/typography'

import { renderTypography } from './typography'
import { renderInputs } from './buttons'
import { renderComponents } from './components'

type PropData = {
  name: string
  type: string
  required: string
  description: string
}

@Component
export class PatternLibraryRoute extends Vue {

  render (h: CreateElement) {
    const typography = renderTypography(h)
    const inputs = renderInputs(h)
    const components = renderComponents(h)

    return <div class='pattern-library-page'>

      <el-container>

        <el-aside style='width: 0'>
          <el-menu default-openeds={['3']}>

            <el-menu-item index='1'>
              <template slot='title'>Layout</template>
            </el-menu-item>

            <el-menu-item index='2'>
              <template slot='title'>Typography</template>
            </el-menu-item>

            <el-submenu index='3'>
              <template slot='title'>Components</template>
              {
                components.map((component, index) => {
                  let reference = this.getComponent(component)

                  return !reference ? null :
                    <el-menu-item index={'3-' + (index + 1)}>{
                      getComponentName(reference)
                    }</el-menu-item>
                })
              }
            </el-submenu>

          </el-menu>
        </el-aside>

        <el-main>

          <H4>Typography</H4>

          {
            typography.map((component) => {
              let reference = this.getComponent(component)

              let codeExample = !reference ? null :
                <Prism language='jsx'>{
                  this.getComponentNotation(reference)
                }</Prism>

              return <div>
                <el-row>
                  <el-col span={12}>
                    <div style='padding: 1em; margin: .5em 0; border: 1px solid #e6ebf5;'>{component}</div>
                  </el-col>
                </el-row>
                <el-row>
                  <el-col span={12}>
                    {codeExample}
                  </el-col>
                </el-row>
              </div>
            })
          }

          <H4>Inputs</H4>

          {
            inputs.map((button, index) => {
              let reference = this.getComponent(button[0])

              let codeExample = !reference ? null :
                /* istanbul ignore next: none of the input examples has a ref */
                <Prism language='jsx'>{
                  this.getComponentNotation(reference)
                }</Prism>

              return <div>
                <el-row>
                  <el-col span={12}>
                    {
                      button.map((buttonState) => {
                        return <div style='padding: 0.5em; display: flex; justify-content: space-around'>
                          {
                            buttonState
                          }

                        </div>
                      })
                    }
                  </el-col>
                </el-row>
                <el-row>
                  <el-col span={12}>
                    {codeExample}
                  </el-col>
                </el-row>
              </div>
            })
          }

          <H4>Components</H4>

          {
            components.map((component) => this.renderComponent(h, component))
          }

        </el-main>
      </el-container>

    </div>

  }

  private renderComponentProps (h: CreateElement, propData: PropData[]) {
    return <el-table data={propData}
                     border={true} show-header={true}
                     empty-text={'This component does not have any properties.'}>
      <el-table-column label='Name' property='name' class-name={'code prop'}></el-table-column>
      <el-table-column label='Type' property='type' class-name={'code type'}></el-table-column>
      <el-table-column label='Required' property='required'></el-table-column>
      <el-table-column label='Description' property='description' width={600}></el-table-column>
    </el-table>
  }

  private renderComponentPreview (h: CreateElement, component: VNode) {
    return <div class='desktop' style='display: flex; flex-direction: column; justify-content: center; align-items: center; margin: 1em'>

      {
        component
      }

      <div style='position: absolute; top: 0; left: 0; z-index: 1'>
        <button style='margin-right: 0.25em' onClick={
          ($event) => {
            $event.target.parentNode.parentNode.className = 'desktop'
          }
        }>Desktop
        </button>

        <button onClick={
          ($event) => {
            $event.target.parentNode.parentNode.className = 'mobile'
          }
        }>Mobile
        </button>
      </div>

    </div>
  }

  private renderComponent (h: CreateElement, component: VNode | VNode[]) {
    const hasVariants = component instanceof Array

    let reference = this.getComponent(hasVariants ? component[0] : component)

    let title
    let description

    let propData: PropData[] = []

    let codeExample

    if (reference) {
      let componentName = getComponentName(reference)

      title = <H6>{componentName} Component</H6>
      description = <Body1>{Annotate.get(`${componentName}`)}</Body1>

      getComponentProps(reference).map((prop) => {
        propData.push({
          name: prop.name,
          type: prop.type,
          required: prop.required ? 'yes' : 'no',
          description: Annotate.get(`${componentName}.${prop.name}`)
        })
      })

      codeExample = <Prism language='jsx'>{
        this.getComponentNotation(reference)
      }</Prism>
    }

    return <div>

      <el-row>

        <el-col span={24}>

          {title}

          {description}

        </el-col>

      </el-row>

      <el-row style='display: flex; align-items: center'>

        <el-col span={8}>

          {
            hasVariants ? (component as VNode[]).map((variant) => {
              return this.renderComponentPreview(h, variant)
            }) : this.renderComponentPreview(h, component as VNode)
          }

        </el-col>

        <el-col span={16}>

          <H6>Properties</H6>

          {
            this.renderComponentProps(h, propData)
          }

        </el-col>

      </el-row>

      <el-row>

        <el-col span={24}>

          <H6>Code example</H6>

          {codeExample}

        </el-col>

      </el-row>

    </div>
  }

  private getComponent (node: VNode) {
    if (node.data && node.data.ref) {
      return this.$refs[node.data.ref as string] as Vue
    }

    return null
  }

  private getComponentNotation (component: Vue, slot = 'default', i = 0) {
    function indent (notation: string, len: number) {
      let i = -1

      while (++i < len) {
        notation = '\t' + notation
      }

      return notation
    }

    const name = getComponentName(component)
    const props = getComponentProps(component)

    let jsx = indent('<' + name, i)

    // slot name
    if (slot !== 'default') {
      jsx += ' slot={"' + slot + '"}'
    }

    // props
    for (let data of props) {
      if (data.value !== undefined) {
        jsx += ' ' + data.name + '='

        if (typeof data.value === 'string') {
          jsx += '{"' + data.value + '"}'
        } else if (typeof data.value === 'object') {
          // jsx += '{' + JSON.stringify(data.value, null, '\t') + '}'
          jsx += '{...}'
        } else {
          jsx += '{' + data.value + '}'
        }
      }
    }

    jsx += '>'

    let textOnly = true

    for (let slot in component.$slots) {
      let nodes = component.$slots[slot] || []

      let i2 = i + 1

      for (let node of nodes) {
        if (node.text) {
          jsx += node.text
        } else if (node.data && node.data.ref) {
          textOnly = false

          let component = this.$refs[node.data.ref as string] as Vue

          jsx += '\n' + this.getComponentNotation(component, slot, i2)
        }
      }
    }

    if (textOnly) {
      jsx += '</' + name + '>'
    } else {
      jsx += '\n' + indent('</' + name + '>', i)
    }

    return jsx
  }

}

function getComponentName (component: Vue) {
  return component.$options.name
}

function getComponentProps (component: Vue) {
  const props: {
    name: string
    type: string
    value: any
    required: boolean
  }[] = []

  if (component.$options.props) {
    for (let propName in component.$options.props) {
      const prop = component.$options.props[propName]
      const propType = formatType(component, prop.type)

      props.push({
        name: propName,
        type: propType,
        value: component[propName],
        required: !!prop.required
      })
    }
  }

  return props
}

/*function getComponentSlots (component: Vue) {
          let slots: string[] = []

  for (let slot in component.$slots) {
          slots.push(slot)
        }

        return slots
}*/

function formatType (component: Vue, type: any) {
  if (typeof type === 'function') {
    let match = (type.toString() as string).match(/^(?:function|class) (\w+)/)

    if (match && match.length > 1) {
      return match[1]
    }
  }

  return 'any'
}
